/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { useRef, useState } from "react";

import { useWindowSize } from "@react-hook/window-size";
import Link from "next/link";
import { useRouter } from "next/router";
import { isElectron, isMobile } from "react-device-detect";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import { useCurrentUser } from "src/app/useCurrentUser";
import { Plate } from "src/classes/plate/Plate";
import { CurrentUserAvatar } from "src/components/AppTopbar/CurrentUserAvatar";
import { StyledAppTobar, StyledTopbarLeft, StyledTopbarRight } from "src/components/AppTopbar/styles";
import { COMMAND_SYMBOL } from "src/components/Hotkeys/hotkeys";
import { UnstyledLink } from "src/components/UI/Links/Links";
import { Tooltip } from "src/components/UI/Tooltip";
import { searchActions } from "src/store/modules/search";

import { NavButtons } from "./NavButtons";
import { UserDropdown } from "./UserDropdown";

export function AppTopbar() {
  const dispatch = useDispatch();
  const router = useRouter();
  const avatarRef = useRef(null);
  const [windowWidth] = useWindowSize();

  const currentUser = useCurrentUser();
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const handleAvatarClick = () => {
    setUserDropdownOpen(!userDropdownOpen);
  };

  return (
    <>
      <StyledAppTobar onDoubleClick={() => (window as any).electronAPI?.send("doubleClick")}>
        <StyledTopbarLeft>
          {isElectron && windowWidth > 530 && <NavButtons />}
          {currentUser ? (
            <>
              <Link href="/home/mine" passHref>
                <UnstyledLink>
                  <TopbarTextItem
                    $active={router.pathname?.startsWith("/home")}
                    onDoubleClick={(e) => e.stopPropagation()}
                  >
                    HOME
                  </TopbarTextItem>
                </UnstyledLink>
              </Link>
              <Link href="/explore" passHref>
                <UnstyledLink>
                  <TopbarTextItem
                    $active={router.pathname?.startsWith("/explore")}
                    onDoubleClick={(e) => e.stopPropagation()}
                  >
                    EXPLORE
                  </TopbarTextItem>
                </UnstyledLink>
              </Link>
            </>
          ) : (
            <Link href="/meta" passHref>
              <UnstyledLink>
                <TopbarTextItem onDoubleClick={(e) => e.stopPropagation()}>VIENNA</TopbarTextItem>
              </UnstyledLink>
            </Link>
          )}
        </StyledTopbarLeft>
        <StyledTopbarRight>
          {currentUser ? (
            <>
              {!isMobile && (
                <Tooltip tooltipText={`New Plate (${COMMAND_SYMBOL}N)`} delayShow={1000}>
                  {/* id on TopbarTextItem is getting overwritten without a wrapper div */}
                  <div>
                    <TopbarTextItem
                      id="new-plate-button-top-bar"
                      onClick={() => Plate.create().then((plate) => plate?.open())}
                      onDoubleClick={(e) => e.stopPropagation()}
                    >
                      NEW_PLATE
                    </TopbarTextItem>
                  </div>
                </Tooltip>
              )}
              <Tooltip tooltipText={`Search (${COMMAND_SYMBOL}K)`} delayShow={1000}>
                <TopbarTextItem
                  onClick={() => dispatch(searchActions.searchModalOpened({ modalType: "search" }))}
                  onDoubleClick={(e) => e.stopPropagation()}
                >
                  SEARCH
                </TopbarTextItem>
              </Tooltip>
              <CurrentUserAvatar ref={avatarRef} handleClick={handleAvatarClick} />
            </>
          ) : (
            <Link href="/signin" passHref>
              <UnstyledLink>
                <TopbarTextItem $active={false}>SIGN_IN </TopbarTextItem>
              </UnstyledLink>
            </Link>
          )}
        </StyledTopbarRight>
        <UserDropdown
          rendered={userDropdownOpen}
          closeDropdown={() => setUserDropdownOpen(false)}
          avatarRef={avatarRef}
        />
      </StyledAppTobar>
    </>
  );
}

interface Disablable {
  disabled?: boolean;
  $active?: boolean;
}

export const TopbarItem = styled.div<Disablable>`
  color: ${({ theme, disabled, $active }) =>
    disabled ? theme.text.disabled : $active ? theme.text.default : theme.text.subtle};
  user-select: none;
  &:hover {
    color: ${({ theme, disabled }) => !disabled && theme.text.default};
  }
  transition: all 150ms ease;
  -webkit-app-region: no-drag;
`;

const TopbarTextItem = styled(TopbarItem)`
  padding-right: 10px;
  padding-left: 10px;
  white-space: nowrap;
  align-items: center;
  font-weight: bold;
  transition: all 150ms ease;
  cursor: pointer;
`;
